"use client"

import { useAuth } from "@/contexts/auth-context"
import { env } from "@/lib/env"
import { getBasePathUrl } from "@/lib/base-path"
import { tokenManager } from "@/lib/token-manager"

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public errorData?: any
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Extracts the server's error detail from an ApiError. The PayrollIntegrations
 * function endpoints return `{ error: "..." }` bodies; Web API BadRequest
 * bodies use Message/message/detail.
 */
export function getApiErrorDetail(err: unknown): string | null {
  if (err instanceof ApiError) {
    return (
      err.errorData?.error ??
      err.errorData?.Message ??
      err.errorData?.message ??
      err.errorData?.detail ??
      null
    )
  }
  return null
}

const pascalToCamel = (str: string): string => str.charAt(0).toLowerCase() + str.slice(1)

/**
 * Recursively transforms object keys from PascalCase (Web API responses) to
 * camelCase. Writes don't need the inverse — model binding is case-insensitive.
 */
export function transformPascalToCamel(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(transformPascalToCamel)
  }
  if (obj !== null && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [pascalToCamel(key), transformPascalToCamel(value)])
    )
  }
  return obj
}

// API endpoints
export const API_ENDPOINTS = {
  // Admin Service API - GlobalConfiguration document CRUD.
  // PaymentFileFormatType / PayrollProviderConfiguration / mapping documents
  // are AdminService-owned; this MFE never writes them through the Function App.
  ADMIN: {
    BASE_URL: env.adminServiceApiEndpoint,
    CONFIG_BY_GROUP: (groupName: string) => `/GlobalConfiguration/Configuration/${encodeURIComponent(groupName)}`,
    // Tenant theme customization (same document appNext's ThemeService reads)
    TENANT_THEME: "/GlobalConfiguration/Configuration/TenantTheme",
  },
  // PayrollIntegrations Function App - migrations, credentials, test-connection
  PAYROLL: {
    BASE_URL: env.payrollIntegrationsApiUrl,
    MIGRATIONS: "/migrations",
    MIGRATION_STATUS_COUNTS: "/migrations/status-counts",
    MIGRATION_DETAIL: (provider: string, userProfileId: number, internalOrgId: number) =>
      `/migrations/${encodeURIComponent(provider)}/${userProfileId}/${internalOrgId}`,
    MIGRATION_REQUEST_PREVIEW: (provider: string, userProfileId: number, internalOrgId: number) =>
      `/migrations/${encodeURIComponent(provider)}/${userProfileId}/${internalOrgId}/request-preview`,
    MIGRATION_RETRY: (provider: string, userProfileId: number, internalOrgId: number) =>
      `/migrations/${encodeURIComponent(provider)}/${userProfileId}/${internalOrgId}/retry`,
    MIGRATION_PUSH: (provider: string, userProfileId: number, internalOrgId: number) =>
      `/migrations/${encodeURIComponent(provider)}/${userProfileId}/${internalOrgId}/push`,
    MIGRATION_EDIT_RETRY: (provider: string, userProfileId: number, internalOrgId: number) =>
      `/migrations/${encodeURIComponent(provider)}/${userProfileId}/${internalOrgId}/edit-retry`,
    MIGRATION_RESOLVE: (provider: string, userProfileId: number, internalOrgId: number) =>
      `/migrations/${encodeURIComponent(provider)}/${userProfileId}/${internalOrgId}/resolve`,
    MIGRATION_IGNORE: (provider: string, userProfileId: number, internalOrgId: number) =>
      `/migrations/${encodeURIComponent(provider)}/${userProfileId}/${internalOrgId}/ignore`,
    MIGRATION_SAVE_PAYLOAD: (provider: string, userProfileId: number, internalOrgId: number) =>
      `/migrations/${encodeURIComponent(provider)}/${userProfileId}/${internalOrgId}/payload`,
    BACKFILL_CANDIDATES: "/backfill/candidates",
    BACKFILL_RUN: "/backfill/run",
    PROFILE_TYPES: "/lookups/profile-types",
    INTERNAL_ORGANIZATIONS: "/lookups/internal-organizations",
    VALUE_OPTIONS: (field: string) => `/lookups/value-options/${encodeURIComponent(field)}`,
    CREDENTIALS: "/providers/credentials",
    TEST_CONNECTION: (provider: string) => `/providers/${encodeURIComponent(provider)}/test-connection`,
  },
}

// Custom hook for making authenticated API requests
export function useApi() {
  const { token, requestTokenFromParent } = useAuth()

  // Generic fetch function with authentication and token retry logic
  const fetchWithAuth = async <T,>(url: string, options: RequestInit = {}): Promise<T> => {
    const headers = new Headers(options.headers)

    // If no token is available, request one and queue this request
    if (!token) {
      // Request token from parent if we haven't already
      if (tokenManager.shouldRequestToken()) {
        requestTokenFromParent()
      }

      // Apply base path if the URL is relative and doesn't start with http or //
      let finalUrl = url
      if (!url.startsWith("http") && !url.startsWith("//")) {
        finalUrl = getBasePathUrl(url)
      }
      const retriedResponse = await tokenManager.queueRequest(finalUrl, {
        ...options,
        headers,
      })

          // Process the retried response
          if (!retriedResponse.ok) {
            const errorData = await retriedResponse.json().catch(() => ({}))
            throw new ApiError(
              `API request failed with status ${retriedResponse.status}`,
              retriedResponse.status,
              errorData
            )
          }

      // For non-JSON responses, return the response object
      const contentType = retriedResponse.headers.get("content-type")
      if (contentType && contentType.includes("application/json")) {
        return retriedResponse.json()
      }

      return retriedResponse as unknown as T
    }

    // Add authorization header if token exists
    headers.set("Authorization", `Bearer ${token}`)

    // Apply base path if the URL is relative and doesn't start with http or //
    let finalUrl = url
    if (!url.startsWith("http") && !url.startsWith("//")) {
      finalUrl = getBasePathUrl(url)
    }

    try {
      const response = await fetch(finalUrl, {
        ...options,
        headers,
      })

      if (!response.ok) {
        // Handle 401 Unauthorized errors
        if (response.status === 401) {
          // Request a new token from parent
          if (tokenManager.shouldRequestToken()) {
            requestTokenFromParent()
          }
          const retriedResponse = await tokenManager.queueRequest(finalUrl, {
            ...options,
            headers,
          })

          // Process the retried response
          if (!retriedResponse.ok) {
            const errorData = await retriedResponse.json().catch(() => ({}))
            throw new ApiError(
              `API request failed with status ${retriedResponse.status}`,
              retriedResponse.status,
              errorData
            )
          }

          // For non-JSON responses, return the response object
          const contentType = retriedResponse.headers.get("content-type")
          if (contentType && contentType.includes("application/json")) {
            return retriedResponse.json()
          }

          return retriedResponse as unknown as T
        }

        // Handle other error responses
        const errorData = await response.json().catch(() => ({}))
        throw new ApiError(
          `API request failed with status ${response.status}`,
          response.status,
          errorData
        )
      }

      // For non-JSON responses, return the response object
      const contentType = response.headers.get("content-type")
      if (contentType && contentType.includes("application/json")) {
        const jsonResponse = await response.json()
        return jsonResponse
      }

      return response as unknown as T
    } catch (error) {
      // Rethrow the error
      throw error
    }
  }

  // Helper methods for common HTTP methods
  const get = <T,>(endpoint: string, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, { ...options, method: "GET" })

  const post = <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, {
      ...options,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: JSON.stringify(data),
    })

  const put = <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, {
      ...options,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: JSON.stringify(data),
    })

  const patch = <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, {
      ...options,
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: JSON.stringify(data),
    })

  const del = <T,>(endpoint: string, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, { ...options, method: "DELETE" })

  // Create full URLs for each API
  const createUrl = (baseUrl: string, path: string) => {
    // Remove trailing slash from base URL if present
    const base = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl
    // Remove leading slash from path if present
    const cleanPath = path.startsWith("/") ? path.slice(1) : path
    return `${base}/${cleanPath}`
  }

  // API-specific methods
  const adminApi = {
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    patch: <T,>(path: string, data: any, options?: RequestInit) =>
      patch<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
  }

  const payrollApi = {
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.PAYROLL.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.PAYROLL.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.PAYROLL.BASE_URL, path), data, options),
    patch: <T,>(path: string, data: any, options?: RequestInit) =>
      patch<T>(createUrl(API_ENDPOINTS.PAYROLL.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.PAYROLL.BASE_URL, path), options),
  }

  return {
    adminApi,
    payrollApi,
    fetchWithAuth,
  }
}
